import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Calendar, DollarSign, ShoppingBag, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ReportsPage() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    {
      id: 'sales',
      title: 'Sales Report',
      description: 'Daily, weekly, and monthly sales data',
      icon: DollarSign,
      color: 'green'
    },
    {
      id: 'orders',
      title: 'Orders Report',
      description: 'Complete order history and details',
      icon: ShoppingBag,
      color: 'blue'
    },
    {
      id: 'items',
      title: 'Menu Items Performance',
      description: 'Best selling items and performance',
      icon: TrendingUp,
      color: 'purple'
    },
    {
      id: 'customers',
      title: 'Customer Report',
      description: 'Customer orders and preferences',
      icon: Users,
      color: 'orange'
    },
    {
      id: 'tables',
      title: 'Table Utilization',
      description: 'Table occupancy and turnover rates',
      icon: Calendar,
      color: 'red'
    }
  ];

  const downloadReport = async (reportType) => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }

    setLoading(true);
    try {
      // Fetch data based on report type
      let data;
      switch (reportType) {
        case 'sales':
          data = await fetchSalesReport();
          break;
        case 'orders':
          data = await fetchOrdersReport();
          break;
        case 'items':
          data = await fetchItemsReport();
          break;
        case 'customers':
          data = await fetchCustomersReport();
          break;
        case 'tables':
          data = await fetchTablesReport();
          break;
        default:
          toast.error('Invalid report type');
          return;
      }

      // Convert to CSV
      const csv = convertToCSV(data);
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report_${dateFrom}_to_${dateTo}.csv`;
      a.click();
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesReport = async () => {
    const response = await axios.get(`${API}/orders`, { 
      headers: getAuthHeader(),
      params: { date_from: dateFrom, date_to: dateTo }
    });
    
    return response.data.map(order => ({
      'Order ID': order.id?.substring(0, 8) || 'N/A',
      'Date': new Date(order.created_at).toLocaleDateString(),
      'Type': order.order_type,
      'Subtotal': order.total_amount || 0,
      'Tax': order.tax || 0,
      'Total': order.grand_total || order.total_amount || 0,
      'Status': order.status || order.order_status || 'N/A',
      'Payment Status': order.payment_status || 'N/A'
    }));
  };

  const fetchOrdersReport = async () => {
    const ordersResponse = await axios.get(`${API}/orders`, { 
      headers: getAuthHeader(),
      params: { date_from: dateFrom, date_to: dateTo }
    });
    
    // Fetch all tables to map table_id to table_name
    const tablesResponse = await axios.get(`${API}/tables`, { 
      headers: getAuthHeader() 
    });
    
    // Create a map of table_id to table_name
    const tableMap = {};
    tablesResponse.data.forEach(table => {
      tableMap[table.id] = table.table_name || table.name || 'Unknown';
    });
    
    return ordersResponse.data.map(order => ({
      'Order ID': order.id?.substring(0, 8) || 'N/A',
      'Date': new Date(order.created_at).toLocaleDateString(),
      'Time': new Date(order.created_at).toLocaleTimeString(),
      'Table': order.table_id ? (tableMap[order.table_id] || 'Table Not Found') : (order.order_type === 'dine_in' ? 'N/A' : '-'),
      'Type': order.order_type,
      'Items Count': order.items?.length || 0,
      'Customer Name': order.customer_name || 'N/A',
      'Customer Phone': order.customer_phone || 'N/A',
      'Total': order.grand_total || order.total_amount || 0,
      'Status': order.status || order.order_status || 'N/A'
    }));
  };

  const fetchItemsReport = async () => {
    const ordersResponse = await axios.get(`${API}/orders`, { 
      headers: getAuthHeader(),
      params: { date_from: dateFrom, date_to: dateTo }
    });

    const orders = ordersResponse.data.data; 

    const itemsMap = {};

    orders.forEach(order => {
      order.items?.forEach(item => {
        const key = item.item_id || item.item_name; 

        if (!itemsMap[key]) {
          itemsMap[key] = {
            name: item.item_name,
            quantity: 0,
            revenue: 0
          };
        }

        itemsMap[key].quantity += item.quantity;
        itemsMap[key].revenue += item.price * item.quantity;
      });
    });

    return Object.entries(itemsMap).map(([id, data]) => ({
      'Item ID': id,
      'Item Name': data.name,
      'Total Quantity Sold': data.quantity,
      'Total Revenue': data.revenue.toFixed(2)
    }));
  };


  const fetchCustomersReport = async () => {
    const response = await axios.get(`${API}/orders`, { 
      headers: getAuthHeader(),
      params: { date_from: dateFrom, date_to: dateTo }
    });
    
    const customersMap = {};
    response.data.forEach(order => {
      const key = order.customer_phone || order.customer_name || 'Walk-in';
      if (!customersMap[key]) {
        customersMap[key] = {
          name: order.customer_name || 'Walk-in',
          phone: order.customer_phone || 'N/A',
          orders: 0,
          totalSpent: 0
        };
      }
      customersMap[key].orders += 1;
      customersMap[key].totalSpent += order.grand_total;
    });

    return Object.values(customersMap).map(customer => ({
      'Customer Name': customer.name,
      'Phone': customer.phone,
      'Total Orders': customer.orders,
      'Total Spent': customer.totalSpent.toFixed(2)
    }));
  };

  const fetchTablesReport = async () => {
    const tablesResponse = await axios.get(`${API}/tables`, { headers: getAuthHeader() });
    const ordersResponse = await axios.get(`${API}/orders`, { 
      headers: getAuthHeader(),
      params: { date_from: dateFrom, date_to: dateTo, order_type: 'dine_in' }
    });
    
    const tableUsage = {};
    ordersResponse.data.forEach(order => {
      if (order.table_id) {
        if (!tableUsage[order.table_id]) {
          tableUsage[order.table_id] = 0;
        }
        tableUsage[order.table_id] += 1;
      }
    });

    return tablesResponse.data.map(table => ({
      'Table Name': table.table_name,
      'Section': table.section,
      'Capacity': table.capacity,
      'Orders Served': tableUsage[table.id] || 0,
      'Status': table.status
    }));
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    // Original headers
    const headers = Object.keys(data[0]);
    
    // Add 'S.No' as the first header
    const csvRows = [];
    csvRows.push(['S.No', ...headers].join(','));
    
    // Add data rows with serial number
    data.forEach((row, index) => {
      const values = headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"`
          : value;
      });
      csvRows.push([index + 1, ...values].join(',')); // index + 1 for serial number
    });
    
    return csvRows.join('\n');
  };


  const viewReport = async (reportType) => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }
    toast.info('View functionality coming soon. Use download for now.');
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Reports</h1>
          <p className="text-gray-600">Generate and download various business reports</p>
        </div>

        {/* Date Range Selection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar size={20} />
              Select Date Range
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_from">From Date</Label>
                <Input
                  id="date_from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="date_to">To Date</Label>
                <Input
                  id="date_to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            return (
              <Card key={report.id} className="hover:shadow-lg transition-all">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-full bg-${report.color}-100 flex items-center justify-center mb-3`}>
                    <Icon className={`text-${report.color}-600`} size={24} />
                  </div>
                  <CardTitle className="text-lg">{report.title}</CardTitle>
                  <p className="text-sm text-gray-600">{report.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => viewReport(report.id)}
                      variant="outline"
                      className="flex-1"
                      disabled={loading}
                    >
                      View
                    </Button>
                    <Button
                      onClick={() => downloadReport(report.id)}
                      className={`flex-1 bg-${report.color}-600 hover:bg-${report.color}-700`}
                      disabled={loading}
                    >
                      <Download size={16} className="mr-2" />
                      {loading ? 'Loading...' : 'Download CSV'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
